import type { GenericAbortSignal } from "axios";
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useMemo,
} from "react";

export interface FilterItem {
  field: string;
  value?: any;
  operator: string;
}

export interface FilterModel {
  items: FilterItem[];
  logicOperator?: "and" | "or";
}

export type ValidRecordModel = {
  [key: string | symbol]: any;
};

export type ValidCreateOneData = {
  id?: string;
  data: {
    id: string;
  };
};

export type ValidUpdateOneData = {
  id?: string;
  data: {
    id: string;
  };
};

export type ValidGetListResponse<
  Record extends ValidRecordModel = ValidRecordModel,
> = {
  data: Record[];
  meta: {
    total: number;
  };
  [key: string]: any;
};

type Pagination = {
  number?: number;
  size?: number;
};

export type SortDirection = "asc" | "desc" | null | undefined;

export interface SortItem {
  field: string;
  sort: SortDirection;
}

export type SortModel = SortItem[];

type ValidDataProviderMetaMap = {
  getList: any;
  deleteOne: any;
  createOne: any;
  updateOne: any;
  getOne: any;
  getCreateFormData: any;
};

export type DataProvider<
  Meta extends ValidDataProviderMetaMap = ValidDataProviderMetaMap,
> = {
  getList: (params: {
    resource: string;
    meta?: Meta["getList"];
    page?: Pagination;
    sort?: SortModel;
    filter?: FilterModel;
    signal?: GenericAbortSignal;
  }) => Promise<ValidGetListResponse>;
  deleteOne: (params: {
    resource: string;
    id: string;
    data?: any;
    meta?: Meta["deleteOne"];
  }) => Promise<any>;
  createOne: <
    ResponseData extends ValidCreateOneData = ValidCreateOneData,
  >(params: {
    resource: string;
    data: any;
    meta?: Meta["createOne"];
  }) => Promise<ResponseData>;
  updateOne: <
    ResponseData extends ValidUpdateOneData = ValidUpdateOneData,
  >(params: {
    resource: string;
    data: any;
    id: string;
    meta?: Meta["updateOne"];
  }) => Promise<ResponseData>;
  getOne: (params: {
    resource: string;
    meta?: Meta["getOne"];
    signal?: GenericAbortSignal;
    id: string;
  }) => Promise<any>;
  getCreateFormData: (params: {
    resource: string;
    meta?: Meta["getCreateFormData"];
    signal?: GenericAbortSignal;
  }) => Promise<any>;

  cloneToLocale: (params: {
    resource: string;
    from_locale: string;
    to_locale: string;
    id: string;
  }) => Promise<any>;
};

type SystemContextValue = {
  dataProvider: DataProvider;
};

export const SystemContext = createContext<null | SystemContextValue>(null);

export const SystemProvider = ({
  dataProvider,
  children,
}: PropsWithChildren<SystemContextValue>) => {
  const context = useMemo(
    () => ({
      dataProvider,
    }),
    [dataProvider],
  );
  return (
    <SystemContext.Provider value={context}>{children}</SystemContext.Provider>
  );
};

export const useSystemProvider = (): SystemContextValue => {
  const context = useContext(SystemContext);
  if (!context) {
    throw new Error("useSystemProvider must be used within SystemProvider");
  }
  return context;
};

export const useDataProvider = <
  DataProviderMetaMap extends
    ValidDataProviderMetaMap = ValidDataProviderMetaMap,
>(): DataProvider<DataProviderMetaMap> => {
  const context = useSystemProvider();
  return context.dataProvider;
};

export type ListQueryKeyParams = {
  page?: {
    number: number;
    size: number;
  };
  isInfinite?: boolean;
  isAggregation?: boolean;
  filter?: FilterModel;
  sort?: SortModel;
  resource: string;

  meta?: any;
};

export const createModuleQueries = () => {
  const queries = {
    all: ({ resource }: { resource: string }) => [resource] as const,
    list: ({
      resource,
      isInfinite = false,
      isAggregation = false,
      ...restParams
    }: ListQueryKeyParams) => {
      return [
        ...queries.lists({ resource }),
        {
          isInfinite,
          isAggregation,
          ...restParams,
        },
      ] as const;
    },
    lists: ({ resource }: { resource: string }) =>
      [...queries.all({ resource }), "list"] as const,
    details: ({ resource }: { resource: string }) =>
      [...queries.all({ resource }), "detail"] as const,
    detail: (params: { id: string; meta?: any; resource: string }) => {
      const { resource, id, ...restParams } = params;

      return [
        ...queries.details({
          resource,
        }),
        id,
        restParams,
      ] as const;
    },
    allCreateForms: ({ resource }: { resource: string }) => {
      return [...queries.all({ resource }), "createForm"];
    },
    createFormData: ({
      resource,
      ...restParams
    }: {
      resource: string;
      meta?: any;
    }) => {
      return [...queries.allCreateForms({ resource }), "data", restParams];
    },
  } as const;
  return queries;
};

export const useResourceQueries = () => {
  return useMemo(() => createModuleQueries(), []);
};
